import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { passwordPolicyError } from "../lib/password";
import { getSessionToken } from "../lib/authSession";

/**
 * BD1.4 — app-level forced password backfill.
 *
 * Safety net that catches every login origin the /login flow can't see:
 * Google OAuth returns, stale pre-BD1 sessions, deep links. On any authed
 * route it asks GET /api/auth/password-status once; a passwordless account
 * gets a NON-DISMISSABLE modal (no close button, no backdrop click, Escape
 * ignored) until a password is set via POST /api/auth/set-password.
 *
 * Skipped on /login (that flow runs its own in-flow SetPasswordScreen) and
 * /admin* (admin auth is a separate cookie world). The "ok" verdict is cached
 * for the app lifetime so this costs one request per session.
 */
export function PasswordBackfillGate() {
  const location = useLocation();
  const [needsPassword, setNeedsPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // "ok" is sticky for the session; "unknown" re-checks on route change.
  const verdictRef = useRef<"unknown" | "ok">("unknown");

  const onExemptRoute =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/admin");

  useEffect(() => {
    if (onExemptRoute || verdictRef.current === "ok" || needsPassword) return;
    const token = getSessionToken();
    if (!token) return; // logged out — nothing to enforce
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/auth/password-status", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.status === 401) {
          // Dead session — the app's own auth handling deals with it.
          verdictRef.current = "ok";
          return;
        }
        const data = await r.json().catch(() => ({}));
        if (data && data.has_password === false) {
          setEmail(window.localStorage.getItem("bsure_email") || "");
          setNeedsPassword(true);
        } else if (data && data.has_password === true) {
          verdictRef.current = "ok";
        }
      } catch {
        /* network blip — try again on the next route change */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, onExemptRoute, needsPassword]);

  if (!needsPassword || onExemptRoute) return null;

  const policyError = touched ? passwordPolicyError(password) : null;
  const confirmError =
    touched && confirm && confirm !== password ? "Passwords don't match." : null;
  const canSubmit =
    !passwordPolicyError(password) && confirm === password && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setSubmitError(null);
    if (!canSubmit) return;
    setLoading(true);
    try {
      const r = await fetch("/api/auth/set-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getSessionToken()}`,
        },
        body: JSON.stringify({ password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setSubmitError(
          (data && (data.message || data.error)) ||
            "Could not save your password. Please try again.",
        );
        setLoading(false);
        return;
      }
      verdictRef.current = "ok";
      setNeedsPassword(false);
      setLoading(false);
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bd1-backfill-title"
      // Intentionally NO onClick-to-dismiss: the backfill is mandatory.
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div className="bsure-ui w-full max-w-md rounded-2xl bg-[var(--color-paper)] p-6 shadow-xl">
        <h2
          id="bd1-backfill-title"
          className="bsure-display text-[1.35rem] leading-[1.15] text-[var(--color-ink)]"
        >
          Secure your account
        </h2>
        <p className="mt-2 text-[13.5px] leading-[1.55] text-[var(--color-ink-60)]">
          Accounts now use a password for faster sign-in
          {email ? (
            <>
              . Set one for{" "}
              <span className="text-[var(--color-ink)] font-medium">{email}</span>{" "}
              to continue.
            </>
          ) : (
            " . Set one to continue."
          )}
        </p>

        <form onSubmit={handleSubmit} noValidate className="mt-5 flex flex-col gap-4">
          {submitError ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-[13px] leading-snug text-red-900"
            >
              {submitError}
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              autoFocus
              placeholder="At least 8 characters, a letter and a number"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched(true)}
              error={policyError}
            />
            <PasswordStrengthMeter password={password} />
          </div>

          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            placeholder="Type it again"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            onBlur={() => setTouched(true)}
            error={confirmError}
          />

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!password || !confirm}
          >
            Set password and continue
          </Button>
        </form>
      </div>
    </div>
  );
}

export default PasswordBackfillGate;
